import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import { asList, asRecord, erased, hasValue } from "./AllMattersV2Fields";
import {
  KeyValues,
  PageLink,
  PartyTable,
  PartyName,
  RankBadge,
  SourceLines,
  Tag,
  erasedTextSx,
  formatDate,
  receipt,
  rightTone,
  text,
  yen,
} from "./AllMattersV2Parts";
import {
  JointCatalogChip,
  findJointCatalog,
} from "./AllMattersV2JointSecurity";

import { CorporateNumbers } from "./AllMattersV2CorporateNumbers";

const amount = (value: unknown): string => {
  const record = asRecord(value);
  return text(record.原文) || yen(record.円 ?? value);
};

/** Values of a name/address change annotation (付記の名変), as `住所 …`. */
const change = (value: unknown): string => {
  const record = asRecord(value);
  return (["対象者", "住所", "氏名", "名称", "本店"] as const)
    .filter((key) => hasValue(record[key]))
    .map((key) => `${key === "対象者" ? "対象" : key} ${text(record[key])}`)
    .join("\n");
};

const usufruct = (value: unknown): Array<[string, string]> => {
  const record = asRecord(value);
  return (
    [
      "目的",
      "範囲",
      "地代",
      "借賃",
      "支払期",
      "存続期間",
      "敷金",
      "特約",
      "発生条件",
      "承役地",
      "要役地",
    ] as const
  ).map((key) => [key, text(record[key])]);
};

const PurposeTags = ({
  entry,
}: {
  entry: Record<string, unknown>;
}): JSX.Element => {
  const purpose = asRecord(entry.目的);
  const kind = text(purpose.種別);
  const action = text(purpose.動作);
  return (
    <>
      {kind && kind !== "その他" && <Tag label={kind} tone={rightTone(kind)} />}
      {action && action !== "その他" && (
        <Tag
          label={action}
          tone={action === "抹消" ? "default" : "secondary"}
        />
      )}
      {hasValue(purpose.処分制限) && purpose.処分制限 !== "なし" && (
        <Tag label={text(purpose.処分制限)} tone="error" filled />
      )}
      {purpose.仮登記 === true && (
        <Tag
          label={purpose.請求権 === true ? "仮登記（請求権）" : "仮登記"}
          tone="warning"
        />
      )}
      {purpose.持分対象 === true && <Tag label="持分" tone="secondary" />}
      {purpose.共有者全員 === true && <Tag label="共有者全員" />}
      {entry.職権行 === true && <Tag label="職権" />}
    </>
  );
};

const RegisterCard = ({
  entry,
  section,
  current,
  onShowPage,
  jointCatalogs = [],
  onOpenJointCatalog,
}: {
  entry: unknown;
  section: "甲区" | "乙区";
  current?: string;
  onShowPage: (page: number) => void;
  /** 共同担保目録 of the result; chips that match one open it. */
  jointCatalogs?: unknown[];
  onOpenJointCatalog?: (index: number) => void;
}): JSX.Element => {
  const item = asRecord(entry);
  const rank = asRecord(item.順位番号);
  const purpose = asRecord(item.目的);
  const rankText =
    typeof item.順位番号 === "string"
      ? item.順位番号
      : text(rank.原文) || (item.職権行 === true ? "職権" : "—");
  const appendix = hasValue(rank.付記番号) || rankText.includes("付記");
  const purposeText =
    typeof item.目的 === "string"
      ? item.目的
      : text(purpose.原文) || "（目的の記載なし）";
  const isErased = erased(item);
  const tone = isErased ? "default" : rightTone(purpose.種別);
  const head = [
    receipt(item.受付),
    hasValue(item.主順位)
      ? appendix
        ? `主登記 ${text(item.主順位)}番`
        : `${text(item.主順位)}番に併記`
      : "",
  ].filter(Boolean);
  const parties = [
    ...asList(item.権利者),
    ...asList(item.債務者).map((party) => ({
      ラベル: "債務者",
      ...asRecord(party),
    })),
    ...asList(item.連帯債務者).map((party) => ({
      ラベル: "連帯債務者",
      ...asRecord(party),
    })),
    ...asList(item.指定債務者).map((party) => ({
      ラベル: "指定債務者",
      ...asRecord(party),
    })),
  ];
  const sourceLines = asList(item.事項).flatMap((field) =>
    asList(asRecord(field).行)
  );
  return (
    <Paper
      variant="outlined"
      component="article"
      aria-label={`${section} ${rankText} ${purposeText}`}
      sx={(theme) => ({
        p: { xs: 1.5, sm: 2 },
        borderLeft: 4,
        borderLeftColor:
          tone === "default" ? theme.palette.divider : theme.palette[tone].main,
        ...(isErased
          ? { borderStyle: "dashed", bgcolor: theme.palette.action.hover }
          : {}),
      })}
    >
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        <RankBadge label={rankText} appendix={appendix} />
        <Typography
          fontWeight={700}
          sx={{ overflowWrap: "anywhere", ...(isErased ? erasedTextSx : {}) }}
        >
          {purposeText}
        </Typography>
        <PurposeTags entry={item} />
        {current && <Tag label={current} tone="success" filled />}
        {isErased && <Tag label="抹消済み・抹消推定" filled />}
        {head.length > 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: "auto", whiteSpace: "nowrap" }}
          >
            {head.join("　")}
          </Typography>
        )}
      </Stack>
      <Stack
        spacing={1.25}
        mt={1.25}
        sx={isErased ? { color: "text.secondary" } : undefined}
      >
        <KeyValues
          items={[
            ["原因", text(item.原因)],
            ["登記日", formatDate(item.登記日付)],
            [
              "変更後",
              hasValue(item.変更) ? (
                <Box>
                  {change(item.変更)}
                  <CorporateNumbers party={item.変更} />
                  {hasValue(asRecord(item.変更).会社法人等番号) && (
                    <Typography variant="caption" component="div">
                      会社法人等番号（登記記載）:{" "}
                      {text(asRecord(item.変更).会社法人等番号)}
                    </Typography>
                  )}
                  {hasValue(asRecord(item.変更).国内連絡先) && (
                    <Box>
                      国内連絡先
                      <PartyName party={asRecord(item.変更).国内連絡先} />
                    </Box>
                  )}
                </Box>
              ) : null,
            ],
          ]}
        />
        <PartyTable parties={parties} />
        <KeyValues
          items={[
            ["債権額", amount(item.債権額)],
            ["極度額", amount(item.極度額)],
            ["元本極度額", amount(item.元本極度額)],
            ["債権の範囲", text(item.債権の範囲)],
            ["利息", text(item.利息)],
            ["利率", text(item.利率)],
            ["損害金", text(item.損害金)],
            ["延滞税の額", text(item.延滞税の額)],
            ["確定期日", text(item.確定期日)],
            ["弁済期", text(item.弁済期)],
            ["分割後の債権", text(item.分割後の債権)],
            ["取扱店", text(item.取扱店)],
            ...usufruct(item.用益権),
            [
              "順位変更",
              asList(item.順位変更)
                .map((rankChange) => {
                  const record = asRecord(rankChange);
                  return `第${text(record.順位)} ${text(record.対象)}`;
                })
                .join("\n"),
            ],
            [
              "買戻特約",
              hasValue(item.買戻特約)
                ? (["売買代金", "契約費用", "期間"] as const)
                    .map((key) => [key, text(asRecord(item.買戻特約)[key])])
                    .filter(([, value]) => value)
                    .map(([key, value]) => `${key} ${value}`)
                    .join("\n")
                : "",
            ],
            ["信託目録", text(item.信託参照)],
            ["代位者", text(item.代位者)],
            ["代位原因", text(item.代位原因)],
            ["持分更正", text(item.持分更正)],
            ["移記・転写", text(item.移記注記)],
          ]}
        />
        {asList(item.共同担保).length > 0 && (
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              共同担保
            </Typography>
            {asList(item.共同担保).map((reference, index) => {
              const catalog = findJointCatalog(jointCatalogs, reference);
              return (
                <JointCatalogChip
                  key={index}
                  reference={reference}
                  onOpen={
                    catalog >= 0 && onOpenJointCatalog
                      ? () => {
                          onOpenJointCatalog(catalog);
                        }
                      : undefined
                  }
                />
              );
            })}
          </Stack>
        )}
        {typeof item.原文 === "string" && item.原文 && (
          <Typography variant="body2">{item.原文}</Typography>
        )}
        <SourceLines
          lines={sourceLines}
          showErased
          label="事項欄の原文"
          collapsedLines={0}
        />
        <PageLink page={item.ページ} onShowPage={onShowPage} />
      </Stack>
    </Paper>
  );
};

/**
 * 甲区・乙区 as one card per registration: rank and purpose on the first line,
 * receipt on the right, then cause, parties, terms and the folded source text.
 */
export const AllMattersV2Register = ({
  section,
  entries,
  currentIds,
  showErased,
  onShowPage,
  jointCatalogs,
  onOpenJointCatalog,
}: {
  section: "甲区" | "乙区";
  entries: unknown[];
  currentIds: unknown;
  showErased: boolean;
  onShowPage: (page: number) => void;
  /** 共同担保目録 of the result; chips that match one open it. */
  jointCatalogs?: unknown[];
  onOpenJointCatalog?: (index: number) => void;
}): JSX.Element => {
  const current = new Set(
    asList(currentIds).filter((id) => typeof id === "string")
  );
  const visible = entries.filter((entry) => showErased || !erased(entry));
  return (
    <Stack spacing={1.5}>
      {!visible.length && (
        <Alert severity="info">
          抹消済み・抹消推定以外の登記はありません。
        </Alert>
      )}
      {visible.map((entry, index) => {
        const id = asRecord(entry).登記ID;
        return (
          <Box key={typeof id === "string" ? id : index}>
            <RegisterCard
              entry={entry}
              section={section}
              current={
                typeof id === "string" && current.has(id)
                  ? section === "甲区"
                    ? "現在の所有者"
                    : "現在有効"
                  : undefined
              }
              onShowPage={onShowPage}
              jointCatalogs={jointCatalogs}
              onOpenJointCatalog={onOpenJointCatalog}
            />
          </Box>
        );
      })}
    </Stack>
  );
};
